import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBlock } from '../../components/ErrorBlock';
import { Loading } from '../../components/Loading';
import { useI18n, useT } from '../../i18n';
import { formatClock, formatDate, intlLocale } from '../../lib/format';
import { deliveryKey, groupByDate, relativeDay, useNotifications } from './useNotifications';

function dayLabel(day: string, locale: string): string {
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(y, m - 1, d);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(intlLocale(locale), { day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }) }).format(date);
}

/** «Повідомлення»: everything we sent (or only wrote down here) about the family's cases. Opening marks all as read. */
export function NotificationsPage() {
  const t = useT();
  const locale = useI18n((s) => s.locale);
  const { items, status, error, nextCursor, fresh, load, more } = useNotifications();

  useEffect(() => {
    void load({ markRead: true });
  }, [load]);

  const groups = groupByDate(items);

  return (
    <>
      <h1 className="text-[28px] leading-tight sm:text-[34px]">{t('notif.title')}</h1>
      <p className="mt-4 max-w-prose text-[16px] leading-relaxed text-txt">{t('notif.intro')}</p>
      {status === 'loading' || status === 'idle' ? <Loading /> : null}
      {status === 'error' && !items.length ? <ErrorBlock error={error} onRetry={() => void load({ markRead: true })} /> : null}
      {status === 'ready' && !items.length ? <p className="mt-8 text-[16px] text-txt">{t('notif.empty')}</p> : null}

      {groups.map((g) => {
        const rel = relativeDay(g.day);
        return (
          <section key={g.day} className="mt-10">
            <h2 className="sec-h">{rel ? t(rel) : dayLabel(g.day, locale)}</h2>
            <ul>
              {g.items.map((n) => {
                const isNew = fresh.includes(n.id);
                return (
                  <li key={n.id} className="row flex flex-col gap-1 py-4">
                    <div className="flex items-baseline justify-between gap-4">
                      <p className={`text-[16px] leading-snug ${isNew ? 'font-medium' : ''}`}>
                        {n.title || n.body}
                        {isNew ? <span className="ml-2 text-[12px] font-normal uppercase tracking-[.12em] text-lab">{t('notif.new')}</span> : null}
                      </p>
                      <time dateTime={n.created_at} className="flex-none text-[13px] text-lab">
                        {formatClock(n.created_at, locale)}
                      </time>
                    </div>
                    {n.title && n.body ? <p className="whitespace-pre-line text-[15px] leading-relaxed text-txt">{n.body}</p> : null}
                    <p className="flex flex-wrap items-center gap-x-4 text-[13px] text-lab">
                      <span title={n.sent_at ? formatDate(n.sent_at, locale) : undefined}>{t(deliveryKey(n))}</span>
                      {n.case_id ? (
                        <Link to={`/cases/${encodeURIComponent(n.case_id)}`} className="link text-[13px]">
                          {t('cases.open')}
                        </Link>
                      ) : null}
                    </p>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      {nextCursor ? (
        <button type="button" className="btn-quiet mt-8 w-full sm:w-auto" onClick={() => void more()}>
          {t('notif.more')}
        </button>
      ) : null}
    </>
  );
}
